import React, {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import andUsLogo from "../../images/andus-logo.png";
import tpcLogo from "../../images/tpc-performance-centre-logo.png";
import luminousBackground from "../../images/andus-tpc-luminous-background.svg";

import {
  ReactComponent as DesktopRelationship,
} from "../../images/andus-tpc-relationship-desktop.svg";

import {
  ReactComponent as MobileRelationship,
} from "../../images/andus-tpc-relationship-mobile.svg";

import "./TPCGroupSection.css";

const MOBILE_BREAKPOINT = "(max-width: 699px)";
const XLINK_NAMESPACE = "http://www.w3.org/1999/xlink";

function ArrowRight({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

function LocationPin() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 10c0 5-8 11-8 11S4 15 4 10a8 8 0 1 1 16 0Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function useMobileRelationship() {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }

    return window.matchMedia(MOBILE_BREAKPOINT).matches;
  });

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);

    const handleChange = (event) => {
      setIsMobile(event.matches);
    };

    setIsMobile(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);

      return () => {
        mediaQuery.removeEventListener("change", handleChange);
      };
    }

    mediaQuery.addListener(handleChange);

    return () => {
      mediaQuery.removeListener(handleChange);
    };
  }, []);

  return isMobile;
}

function updateSvgImageSource(imageElement, source) {
  if (!imageElement) {
    return;
  }

  imageElement.setAttribute("href", source);
  imageElement.setAttributeNS(
    XLINK_NAMESPACE,
    "xlink:href",
    source
  );
}

function TPCRelationshipArtwork() {
  const relationshipRef = useRef(null);
  const isMobile = useMobileRelationship();

  const RelationshipComponent = isMobile
    ? MobileRelationship
    : DesktopRelationship;

  useLayoutEffect(() => {
    const relationshipElement = relationshipRef.current;

    if (!relationshipElement) {
      return;
    }

    /*
     * The supplied relationship SVGs reference the two logo PNGs using
     * their original filenames. Webpack creates compiled URLs for imported
     * images, so those URLs are injected into the SVG before browser paint.
     */
    const embeddedImages = relationshipElement.querySelectorAll(
      "svg image"
    );

    updateSvgImageSource(embeddedImages[0], andUsLogo);
    updateSvgImageSource(embeddedImages[1], tpcLogo);
  }, [isMobile]);

  return (
    <div
      ref={relationshipRef}
      className="tpc-group-section__relationship"
    >
      <RelationshipComponent
        className="tpc-group-section__relationship-svg"
        preserveAspectRatio={
          isMobile ? "none" : "xMidYMid meet"
        }
        role="img"
        aria-label="AndUs, the applied-intelligence technology provider within the Performance Centre Group"
      />
    </div>
  );
}

export default function TPCGroupSection() {
  return (
    <section
      id="company"
      className="tpc-group-section"
      aria-labelledby="tpc-group-title"
    >
      <img
        className="tpc-group-section__background"
        src={luminousBackground}
        alt=""
        aria-hidden="true"
      />

      <div className="tpc-group-section__inner">
        <div className="tpc-group-section__copy">
          <p className="tpc-group-section__eyebrow">
            Part of the Performance Centre Group
          </p>

          <h2 id="tpc-group-title">
            Where intelligence meets performance.
          </h2>

          <p className="tpc-group-section__body">
            AndUs is the applied-intelligence technology provider
            within the TPC Group—building systems that strengthen how
            organisations learn, operate and grow.
          </p>

          <div className="tpc-group-section__location">
            <span className="tpc-group-section__location-icon">
              <LocationPin />
            </span>

            <span>TPC Head Office · Menlyn, Pretoria</span>
          </div>

          <a
            className="tpc-group-section__cta"
            href="https://tradeperformancecentre.com/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Explore the Performance Centre website"
          >
            <span>Explore the Performance Centre</span>
            <ArrowRight />
          </a>
        </div>

        <div className="tpc-group-section__visual">
          <TPCRelationshipArtwork />
        </div>
      </div>
    </section>
  );
}